import React, { useState } from 'react';
import './DetailTable.css';
import type { TaxCalculationWithDetails } from '../domain/tax';

interface DetailTableProps {
  data: TaxCalculationWithDetails | null;
}

interface TableRow {
  category: string;
  item: string;
  annual: number | undefined;
  note: string;
  clickable?: boolean;
  detailKey?: string;
}

const emptyCalculationDetails = {
  title: '',
  steps: [] as { label: string; value: number; formula?: string }[]
};

const DetailTable: React.FC<DetailTableProps> = ({ data }) => {
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  if (!data) {
    return null;
  }

  const toggleRow = (detailKey: string) => {
    setExpandedRow(expandedRow === detailKey ? null : detailKey);
  };

  const getCalculationDetails = (detailKey: string) => {
    switch (detailKey) {
      case 'estimatedIncome':
        return data.calculationDetails?.estimatedIncome || emptyCalculationDetails;
      case 'incomeTax':
        return data.calculationDetails?.incomeTax || emptyCalculationDetails;
      case 'residentTax':
        return data.calculationDetails?.residentTax || emptyCalculationDetails;
      case 'socialInsurance':
        return data.calculationDetails?.socialInsurance || emptyCalculationDetails;
      case 'netIncome':
        return data.calculationDetails?.netIncome || emptyCalculationDetails;
      case 'definedContribution':
        return data.calculationDetails?.definedContribution || emptyCalculationDetails;
      case 'furusatoNozei':
        return data.calculationDetails?.furusatoNozei || emptyCalculationDetails;
      default:
        return null;
    }
  };

  const formatCurrency = (amount: number | undefined): string => {
    if (amount === undefined || amount === null || isNaN(amount)) return '-';
    return `¥${amount.toLocaleString()}`;
  };

  const formatValue = (amount: number | undefined, label: string): string => {
    if (amount === undefined || amount === null || isNaN(amount)) return '-';

    // 税率の場合はパーセント表示
    if (label.includes('税率') || label.includes('率')) {
      return `${amount.toLocaleString()}%`;
    }

    // それ以外は通貨表示
    return `¥${amount.toLocaleString()}`;
  };


  const taxTotal = data.incomeTax !== undefined && data.residentTax !== undefined
    ? data.incomeTax + data.residentTax
    : undefined;

  const tableData: TableRow[] = [
    // 年収・所得関連
    {
      category: '年収・所得',
      item: '推定年収',
      annual: data.annualIncome,
      note: '推定',
      clickable: true,
      detailKey: 'estimatedIncome'
    },
    {
      category: '年収・所得',
      item: '手取り年収',
      annual: data.netAnnualIncome,
      note: '控除後',
      clickable: true,
      detailKey: 'netIncome'
    },
    
    // 税金関連
    {
      category: '税金',
      item: '所得税',
      annual: data.incomeTax,
      note: '概算額',
      clickable: true,
      detailKey: 'incomeTax'
    },
    {
      category: '税金',
      item: '住民税',
      annual: data.residentTax,
      note: '概算額',
      clickable: true,
      detailKey: 'residentTax'
    },
    {
      category: '税金',
      item: '税金合計',
      annual: taxTotal,
      note: '所得税+住民税'
    },
    
    // 社会保険料関連
    {
      category: '社会保険料',
      item: '社会保険料合計',
      annual: data.socialInsurance,
      note: '健康保険等',
      clickable: true,
      detailKey: 'socialInsurance'
    },
    
    // 所得控除
    {
      category: '所得控除',
      item: '確定拠出年金(DC)',
      annual: data.definedContribution,
      note: 'DC・iDeCo',
      clickable: true,
      detailKey: 'definedContribution'
    },

    // 税額控除（住宅ローン控除が適用されたときのみ表示）
    ...(data.housingLoanApplicable
      ? [
          {
            category: '税額控除',
            item: '住宅ローン控除（所得税分）',
            annual: data.housingLoanCreditIncome,
            note: '',
            clickable: true,
            detailKey: 'incomeTax'
          },
          {
            category: '税額控除',
            item: '住宅ローン控除（住民税分）',
            annual: data.housingLoanCreditResident,
            note: '',
            clickable: true,
            detailKey: 'residentTax'
          }
        ]
      : []),

    // 特殊項目
    {
      category: '控除',
      item: 'ふるさと納税上限額（制度上）',
      annual: data.furusatoInstitutionalLimit,
      note: '',
      clickable: true,
      detailKey: 'furusatoNozei'
    },
    {
      category: '控除',
      item: 'ふるさと納税上限額（損しない上限）',
      annual: data.furusatoSafeLimit,
      note: data.furusatoFilingMethod === 'ONE_STOP' ? 'ワンストップ' : '確定申告',
      clickable: true,
      detailKey: 'furusatoNozei'
    },
    // 差額が生じるときのみ表示（住宅ローン控除なし・所得割非課税でも常時表示すると
    // 常に「損する」ように見えるため）。
    ...(data.furusatoSafeDifference > 0
      ? [
          {
            category: '控除',
            item: '制度上の上限との差額',
            annual: data.furusatoSafeDifference,
            note: ''
          }
        ]
      : [])
  ];

  // カテゴリ別にグループ化
  const groupedData = tableData.reduce((acc, row) => {
    if (!acc[row.category]) {
      acc[row.category] = [];
    }
    acc[row.category].push(row);
    return acc;
  }, {} as Record<string, TableRow[]>);

  return (
    <div className="detail-table">
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>項目</th>
              <th>年間額</th>
              <th>備考</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(groupedData).map(([category, rows]) => (
              <React.Fragment key={category}>
                <tr className="category-header">
                  <td colSpan={3}>{category}</td>
                </tr>
                {rows.map((row, index) => (
                  <React.Fragment key={`${category}-${index}`}>
                    <tr 
                      className={`data-row ${row.clickable ? 'clickable' : ''} ${expandedRow === row.detailKey ? 'expanded' : ''}`}
                      onClick={() => row.clickable && row.detailKey && toggleRow(row.detailKey)}
                    >
                      <td className="item-name">
                        {row.item}
                        {row.clickable && (
                          <span className="expand-icon">
                            {expandedRow === row.detailKey ? ' ▼' : ' ▶'}
                          </span>
                        )}
                      </td>
                      <td className="amount annual">{formatCurrency(row.annual)}</td>
                      <td className="note">{row.note}</td>
                    </tr>
                    {row.detailKey && expandedRow === row.detailKey && (
                      <tr className="detail-row">
                        <td colSpan={3}>
                          <div className="calculation-details">
                            {(() => {
                              const details = getCalculationDetails(row.detailKey);
                              return details ? (
                                <div>
                                  <h4>{details.title}</h4>
                                  <div className="detail-steps">
                                    {details.steps.map((step, stepIndex) => (
                                      <div key={stepIndex} className="detail-step">
                                        <span className="step-label">{step.label}:</span>
                                        <span className="step-value">{formatValue(step.value, step.label)}</span>
                                        <span className="step-formula">{step.formula}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ) : null;
                            })()}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DetailTable;
